import { AxiosError } from 'axios';
import { HttpService, HttpServiceResult } from './http.service';
import { UtilityTypeIds } from '@ncss/models/dist/v3';
import {
  FormattedChartLines,
  HistoryChartDateRanges,
  MeterUsageChartPoint,
  SignalStrengthChartPoint,
  NetworkChartsDateRange,
  GatewaySyncChartPoint,
} from 'common';

export class ChartService extends HttpService {
  async getSyncHistory(
    propertyId: string,
    gatewayId: number,
    range: NetworkChartsDateRange
  ): Promise<HttpServiceResult<GatewaySyncChartPoint[]>> {
    try {
      const data = await this.send<GatewaySyncChartPoint[]>({
        method: 'get',
        url: `/Properties/${propertyId}/Gateway/${gatewayId}/SyncHistory`,
        query: { range },
      });
      data.forEach((d) => (d.timestamp = new Date(d.timestamp)));
      return { success: true, data };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e?.response?.data?.error
            ? e.response.data.error
            : 'Failed to retrieve sync history',
      };
    }
  }

  async getSignalStrengthHistory(
    propertyId: string,
    repeaterId: number,
    range: NetworkChartsDateRange
  ): Promise<HttpServiceResult<SignalStrengthChartPoint[]>> {
    try {
      const data = await this.send<SignalStrengthChartPoint[]>({
        method: 'get',
        url: `/Properties/${propertyId}/Repeaters/${repeaterId}/SignalStrength`,
        query: { range },
      });
      data.forEach((d) => (d.timestamp = new Date(d.timestamp)));
      return { success: true, data };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e?.response?.data?.error
            ? e.response.data.error
            : 'Failed to retrieve signal strength history',
      };
    }
  }

  async getLeakSensorHistory(
    unitId: string,
    location: string,
    range: HistoryChartDateRanges
  ): Promise<HttpServiceResult<FormattedChartLines>> {
    try {
      const data = await this.send<FormattedChartLines>({
        method: 'get',
        url: `/Units/${unitId}/LeakSensors/${location}/History`,
        query: { range },
      });
      return {
        success: true,
        data,
      };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to retrieve leak sensor history',
      };
    }
  }

  async getMeterUsageHistory(
    unitId: string,
    utilityTypeId: UtilityTypeIds,
    range: HistoryChartDateRanges
  ): Promise<HttpServiceResult<MeterUsageChartPoint[]>> {
    try {
      const data = await this.send<MeterUsageChartPoint[]>({
        method: 'get',
        url: `/Units/${unitId}/Meters/${utilityTypeId}/Usage`,
        query: { range },
      });
      return {
        success: true,
        data: data.map((d) => {
          d.endTimestamp = new Date(d.endTimestamp);
          d.startTimestamp = new Date(d.startTimestamp);
          return d;
        }),
      };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to retrieve meter read history',
      };
    }
  }
}
